import React, { useState } from "react";
import { api, hasSessionToken, type OwnerAuthStatusView } from "../api";
import { ownerFacingGlobalError } from "../ownerFacingGlobalError";
import { Panel } from "./Panels";

export interface OwnerAuthPanelError {
  status: number;
  requestId: string | null;
  message: string;
}

function configuredLabel(status: OwnerAuthStatusView | null): string {
  if (!status) return "-";
  return status.ownerAuth.configured ? "konfiguriert" : "nicht konfiguriert";
}

function rotationLabel(status: OwnerAuthStatusView | null): string {
  if (!status) return "-";
  return status.ownerAuth.rotationRequired ? "ja" : "nein";
}

function requestId(error: OwnerAuthPanelError | null): string {
  return error?.requestId ?? "-";
}

export function ownerAuthPanelErrorCopy(error: OwnerAuthPanelError): string {
  return ownerFacingGlobalError(error.message);
}

function ownerAuthProofCopy(hasSession: boolean): {
  endpoint: string;
  session: string;
  sentHeader: string;
  forbiddenHeader: string;
  ownerGate: string;
  statusJudgement: string;
} {
  return {
    endpoint: "/api/owner/status",
    session: hasSession ? "Cookie-Session verifiziert" : "Keine verifizierte Browser-Session",
    sentHeader: hasSession ? "HttpOnly-Cookie: simo_session (renderer-unlesbar)" : "HttpOnly-Cookie: fehlt oder nicht verifiziert",
    forbiddenHeader: "x-simo-owner-token und x-simo-session-token werden nicht vom Renderer gesendet",
    ownerGate: "Login entsperrt die Session; Owner-/Admin-Rolle entscheidet geschuetzte Live-Daten.",
    statusJudgement: "401 ist hier korrekt, wenn die SIMO-Session fehlt, abgelaufen ist oder nicht als Owner/Admin akzeptiert wird.",
  };
}

// SIMO-WEB-OWNER-LOGIN-1 — Owner-Session im Web etablieren. Ohne Token (oder bei 401)
// blendet das Panel ein kompaktes Login-Formular ein; nach Erfolg wird das Dashboard neu geladen.
function OwnerLoginForm({ onAuthChanged }: { onAuthChanged?: () => void }): JSX.Element {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryBusy, setRecoveryBusy] = useState(false);
  const [recoveryStatus, setRecoveryStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (!email.trim() || !password || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.login(email.trim(), password);
      if (!res.ok) {
        setError("Login fehlgeschlagen — E-Mail/Passwort prüfen.");
        return;
      }
      setPassword("");
      onAuthChanged?.();
    } catch {
      setError("Login fehlgeschlagen — E-Mail/Passwort prüfen.");
    } finally {
      setBusy(false);
    }
  }

  async function requestRecovery(): Promise<void> {
    if (!email.trim() || recoveryBusy) return;
    setRecoveryBusy(true);
    setRecoveryStatus(null);
    setError(null);
    try {
      await api.requestPasswordReset(email.trim());
      setRecoveryStatus("Recovery-Anfrage notiert. SIMO zeigt keine Kontodetails an.");
    } catch {
      setRecoveryStatus("Recovery konnte nicht gestartet werden. E-Mail prüfen und erneut versuchen.");
    } finally {
      setRecoveryBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="simo-keyboard-safe-editor space-y-2 rounded border border-jarvis-border/45 bg-jarvis-bg/25 p-2" aria-label="Owner-Login">
      <p className="text-[11px] text-jarvis-muted leading-relaxed">
        Als Owner anmelden, um Geräte, Benachrichtigungen und geschützte Aktionen zu sehen.
      </p>
      <input
        type="email"
        autoComplete="username"
        placeholder="E-Mail"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="simo-keyboard-safe-field min-h-11 w-full rounded border border-jarvis-border/60 bg-jarvis-bg/40 px-2 py-1.5 text-base outline-none focus:border-jarvis-accent/60 sm:text-xs"
      />
      <input
        type="password"
        autoComplete="current-password"
        placeholder="Passwort"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        className="simo-keyboard-safe-field min-h-11 w-full rounded border border-jarvis-border/60 bg-jarvis-bg/40 px-2 py-1.5 text-base outline-none focus:border-jarvis-accent/60 sm:text-xs"
      />
      <button
        type="submit"
        disabled={!email.trim() || !password || busy}
        className="min-h-11 w-full rounded bg-jarvis-accent/80 px-2 py-1.5 text-xs font-medium disabled:opacity-50"
      >
        {busy ? "Anmelden…" : "Anmelden"}
      </button>
      <button
        type="button"
        onClick={() => {
          setShowRecovery((value) => !value);
          setRecoveryStatus(null);
        }}
        className="min-h-11 w-full rounded border border-jarvis-border/60 px-2 py-1.5 text-[11px] text-jarvis-muted transition hover:text-jarvis-text"
      >
        Passwort vergessen?
      </button>
      {showRecovery && (
        <div className="simo-keyboard-safe-editor rounded border border-amber-400/30 bg-amber-400/10 p-2 text-[10px] text-amber-100 leading-relaxed">
          <p>
            Gib deine Owner-E-Mail ein. SIMO notiert die Recovery-Anfrage ohne zu verraten, ob ein Konto existiert.
          </p>
          <button
            type="button"
            onClick={requestRecovery}
            disabled={!email.trim() || recoveryBusy}
            className="mt-2 min-h-11 w-full rounded bg-amber-300/85 px-2 py-1.5 text-[11px] font-semibold text-zinc-950 disabled:opacity-50"
          >
            {recoveryBusy ? "Recovery wird notiert…" : "Recovery anfordern"}
          </button>
          {recoveryStatus && <div className="mt-2 break-words text-amber-100">{recoveryStatus}</div>}
        </div>
      )}
      {error && <div className="text-[10px] text-rose-300 break-words">{error}</div>}
    </form>
  );
}

function PasswordChangeForm(): JSX.Element {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canSubmit =
    currentPassword.length > 0 &&
    newPassword.length >= 8 &&
    confirmPassword.length >= 8 &&
    newPassword === confirmPassword &&
    !busy;

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setStatus(null);
    setError(null);
    try {
      const res = await api.changePassword(currentPassword, newPassword);
      if (!res.ok) {
        setError("Passwortänderung wurde abgelehnt.");
        return;
      }
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setStatus("Passwort geändert. Nutze es ab dem nächsten Login.");
    } catch {
      setError("Passwortänderung fehlgeschlagen. Aktuelles Passwort prüfen.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="simo-keyboard-safe-editor rounded border border-jarvis-border/60 bg-jarvis-bg/35 p-2" aria-label="Passwort ändern">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div>
          <div className="text-xs font-semibold text-jarvis-text">Passwort ändern</div>
          <div className="text-[10px] text-jarvis-muted">Aktuelle Session bleibt geschützt; Klartext wird nicht gespeichert.</div>
        </div>
      </div>
      <div className="grid gap-2">
        <input
          type="password"
          autoComplete="current-password"
          placeholder="Aktuelles Passwort"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          className="simo-keyboard-safe-field min-h-11 w-full rounded border border-jarvis-border/60 bg-jarvis-bg/40 px-2 py-1.5 text-base outline-none focus:border-jarvis-accent/60 sm:text-xs"
        />
        <input
          type="password"
          autoComplete="new-password"
          placeholder="Neues Passwort"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="simo-keyboard-safe-field min-h-11 w-full rounded border border-jarvis-border/60 bg-jarvis-bg/40 px-2 py-1.5 text-base outline-none focus:border-jarvis-accent/60 sm:text-xs"
        />
        <input
          type="password"
          autoComplete="new-password"
          placeholder="Neues Passwort bestätigen"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="simo-keyboard-safe-field min-h-11 w-full rounded border border-jarvis-border/60 bg-jarvis-bg/40 px-2 py-1.5 text-base outline-none focus:border-jarvis-accent/60 sm:text-xs"
        />
      </div>
      {newPassword && confirmPassword && newPassword !== confirmPassword && (
        <div className="mt-2 text-[10px] text-amber-200">Die neuen Passwörter stimmen nicht überein.</div>
      )}
      <button
        type="submit"
        disabled={!canSubmit}
        className="mt-2 min-h-11 w-full rounded bg-jarvis-accent/80 px-2 py-1.5 text-xs font-medium disabled:opacity-50"
      >
        {busy ? "Wird geändert…" : "Passwort ändern"}
      </button>
      {status && <div className="mt-2 text-[10px] text-emerald-300 break-words">{status}</div>}
      {error && <div className="mt-2 text-[10px] text-rose-300 break-words">{error}</div>}
    </form>
  );
}

export function OwnerAuthStatusPanel({
  status,
  error,
  onAuthChanged,
}: {
  status: OwnerAuthStatusView | null;
  error: OwnerAuthPanelError | null;
  onAuthChanged?: () => void;
}): JSX.Element {
  const reachable = status?.apiReachable === true && !error;
  const configured = status?.ownerAuth.configured === true;
  const hasSession = hasSessionToken();
  const needsLogin = !hasSession || error?.status === 401;
  const authProof = ownerAuthProofCopy(hasSession);
  const [loggingOut, setLoggingOut] = useState(false);
  const accent = error ? "simo-accent-error" : reachable && configured ? "simo-accent-ok" : "simo-accent-warn";

  async function logout(): Promise<void> {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await api.logout();
      onAuthChanged?.();
    } finally {
      setLoggingOut(false);
    }
  }

  return (
    <Panel
      title="Owner Auth"
      accent={accent}
      action={<span className="kpi text-xs text-jarvis-muted">{reachable ? "API erreichbar" : "API nicht erreichbar"}</span>}
    >
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
          <span className="text-jarvis-muted">Owner Auth</span>
          <span className="text-right">{configuredLabel(status)}</span>
          <span className="text-jarvis-muted">Key ID</span>
          <span className="font-mono text-right truncate" title={status?.ownerAuth.keyId ?? ""}>
            {status?.ownerAuth.keyId ?? "-"}
          </span>
          <span className="text-jarvis-muted">Fingerprint</span>
          <span className="font-mono text-right truncate" title={status?.ownerAuth.fingerprint ?? ""}>
            {status?.ownerAuth.fingerprint ?? "-"}
          </span>
          <span className="text-jarvis-muted">Rotation required</span>
          <span className="text-right">{rotationLabel(status)}</span>
          <span className="text-jarvis-muted">Letzte Request-ID</span>
          <span className="font-mono text-right truncate" title={requestId(error)}>
            {requestId(error)}
          </span>
        </div>
        <div className="rounded border border-jarvis-border/60 bg-jarvis-bg/35 px-2 py-1.5 text-[10px] text-jarvis-muted leading-relaxed">
          <div>Endpoint: <span className="font-mono">{authProof.endpoint}</span></div>
          <div className="font-semibold text-jarvis-text">{authProof.session}</div>
          <div className="font-mono">{authProof.sentHeader}</div>
          <div>{authProof.forbiddenHeader}</div>
          <div>{authProof.ownerGate}</div>
          <div>{authProof.statusJudgement}</div>
        </div>
        {needsLogin ? (
          <OwnerLoginForm onAuthChanged={onAuthChanged} />
        ) : (
          <>
            <PasswordChangeForm />
            <button
              type="button"
              onClick={logout}
              disabled={loggingOut}
              className="rounded border border-jarvis-border/60 px-2 py-1 text-[11px] text-jarvis-muted disabled:opacity-50"
            >
              {loggingOut ? "Abmelden…" : "Abmelden"}
            </button>
          </>
        )}
        <div className="rounded border border-jarvis-border/60 bg-jarvis-bg/35 px-2 py-1.5 text-[10px] text-jarvis-muted leading-relaxed">
          401: Owner Auth fehlt oder wurde abgelehnt. 403: Policy verweigert die Aktion trotz Auth-Kontext.
        </div>
        {error && (
          <div className="text-[10px] text-rose-300 break-words">{ownerAuthPanelErrorCopy(error)}</div>
        )}
      </div>
    </Panel>
  );
}
